import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Handshake, Loader2, Star, X } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import {
  useApplicationMutations,
  useCampaignApplications,
  type CampaignApplication,
} from "@/hooks/useCollaborations";
import { Badge, Button, Tooltip } from "@/components/ui";
import { COUNTRY_LABELS } from "../constants";
import { APPLICATION_TONE } from "../statusTone";

/**
 * The brand's applications inbox, on the campaign it belongs to.
 *
 * Lives here rather than on /colaboraciones because an application is about a
 * campaign, not yet about a working relationship — the collaboration is what
 * exists once the brand starts one, and that is what the inbox tracks.
 */
export default function CampaignApplications({
  campaignId,
  currency,
}: {
  campaignId: string;
  currency: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { applications, loading } = useCampaignApplications(campaignId);
  const { shortlistApplication, rejectApplication, startCollaboration, busy } =
    useApplicationMutations();
  const [error, setError] = useState<string | null>(null);

  const act = async (action: () => Promise<unknown>) => {
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("marketplace.applications.failed"));
    }
  };

  const start = async (application: CampaignApplication) => {
    if (!application.creatorProfile) return;
    setError(null);
    try {
      const collaboration = await startCollaboration(
        campaignId,
        application.creatorProfile.id,
        application.proposedRate,
      );
      // Straight into the room: the brand's next move is to negotiate, and
      // making them find the new row in a list is a step for its own sake.
      navigate(`/colaboraciones/${collaboration.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("marketplace.applications.failed"));
    }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-card p-6 mt-4">
      <h2 className="text-sm font-semibold text-neutral-900 mb-3">
        {t("marketplace.applications.title")}
        {applications.length > 0 && (
          <span className="ml-2 text-xs font-normal text-neutral-400">
            {applications.length}
          </span>
        )}
      </h2>

      {error && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-control px-3 py-2 mb-3">
          {error}
        </p>
      )}

      {loading && applications.length === 0 ? (
        <p className="text-sm text-neutral-400">{t("common.loading")}</p>
      ) : applications.length === 0 ? (
        <p className="text-sm text-neutral-400">{t("marketplace.applications.empty")}</p>
      ) : (
        <ul className="space-y-3">
          {applications.map((application) => {
            const creator = application.creatorProfile;
            const settled =
              application.status === "ACCEPTED" ||
              application.status === "REJECTED" ||
              application.status === "WITHDRAWN";

            return (
              <li
                key={application.id}
                className="border border-neutral-200 rounded-control p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    {creator?.avatarUrl ? (
                      <img
                        src={creator.avatarUrl}
                        alt=""
                        className="w-9 h-9 rounded-control object-cover flex-shrink-0"
                      />
                    ) : (
                      <span className="w-9 h-9 rounded-control bg-neutral-100 text-neutral-400 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                        {creator?.displayName.charAt(0).toUpperCase() ?? "?"}
                      </span>
                    )}

                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-neutral-900 truncate">
                        {creator ? `@${creator.handle}` : "—"}
                      </p>
                      <p className="text-xs text-neutral-500 truncate">
                        {creator?.displayName}
                        {creator?.country && ` · ${COUNTRY_LABELS[creator.country] ?? creator.country}`}
                      </p>
                    </div>
                  </div>

                  <Badge
                    variant={APPLICATION_TONE[application.status]}
                    stamp
                    className="shrink-0"
                  >
                    {t(`marketplace.applications.status.${application.status}`)}
                  </Badge>
                </div>

                {application.proposedRate !== null && (
                  <p className="text-xs text-neutral-600 mt-2 tabular-nums">
                    {t("marketplace.applications.proposedRate")}{" "}
                    <strong className="text-neutral-900">
                      {application.proposedCurrency ?? currency}{" "}
                      {application.proposedRate.toLocaleString("es")}
                    </strong>
                  </p>
                )}

                {application.pitch && (
                  <p className="text-xs text-neutral-600 mt-2 leading-relaxed">
                    {application.pitch}
                  </p>
                )}

                {!settled && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {/* Every one of these moves a turn or closes a door, so
                        every one says what it does before it is clicked. */}
                    <Tooltip label={t("marketplace.applications.tip.start")}>
                      <Button size="sm" disabled={busy} onClick={() => start(application)}>
                        {busy ? <Loader2 className="animate-spin" /> : <Handshake />}
                        {t("marketplace.applications.start")}
                      </Button>
                    </Tooltip>

                    {application.status === "PENDING" && (
                      <Tooltip label={t("marketplace.applications.tip.shortlist")}>
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={busy}
                          onClick={() => act(() => shortlistApplication(application.id))}
                        >
                          <Star />
                          {t("marketplace.applications.shortlist")}
                        </Button>
                      </Tooltip>
                    )}

                    <Tooltip label={t("marketplace.applications.tip.reject")}>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-neutral-400 hover:text-danger-fg"
                        disabled={busy}
                        onClick={() => act(() => rejectApplication(application.id))}
                      >
                        <X />
                        {t("marketplace.applications.reject")}
                      </Button>
                    </Tooltip>
                  </div>
                )}

                {application.status === "ACCEPTED" && (
                  <p className="inline-flex items-center gap-1.5 text-fine text-success-fg mt-3">
                    <Check className="w-3.5 h-3.5" />
                    {t("marketplace.applications.alreadyStarted")}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
